"use client";

import React, {
  Children,
  ReactNode,
  ElementType,
  ComponentPropsWithoutRef,
} from "react";
import { motion, AnimatePresence } from "framer-motion";
import { cn } from "@/lib/utils";

interface AnimatedHeadingBaseProps {
  children: React.ReactNode;
  delayStep?: number;
  duration?: number;
  className?: string;
  innerClassName?: string;
  splitText?: boolean;
  playOnView?: boolean;
}

type AnimatedHeadingProps<T extends ElementType> = AnimatedHeadingBaseProps & {
  as?: T;
} & Omit<ComponentPropsWithoutRef<T>, keyof AnimatedHeadingBaseProps>;

export default function AnimatedHeading<T extends ElementType = "div">({
  as,
  children,
  delayStep = 0.1,
  duration = 0.5,
  className,
  innerClassName,
  splitText = false,
  playOnView = false,
  ...rest
}: AnimatedHeadingProps<T>) {
  // The wrapper element (default to "div" if `as` is not provided)
  const Wrapper = as || "div";

  // If splitText is true and children is a string, split it by spaces
  let items: ReactNode[] = [];
  if (splitText && typeof children === "string") {
    items = children.split(" ").map((word, i) => <span key={i}>{word} </span>);
  } else {
    // Convert children to an array of React nodes
    items = Children.toArray(children);
  }

  return React.createElement(
    Wrapper,
    {
      className: cn(
        "text-xl md:text-4xl font-semibold text-white font-rubik flex items-baseline justify-center flex-wrap gap-x-2",
        className
      ),
      ...rest,
    },
    <AnimatePresence>
      {items.map((item, index) =>
        item === "\n" ? (
          <span key={index} className="w-full block" />
        ) : (
          <motion.span
            key={index}
            initial={{ filter: "blur(10px)", opacity: 0, y: 12 }}
            animate={
              playOnView ? undefined : { filter: "blur(0px)", opacity: 1, y: 0 }
            }
            whileInView={
              playOnView ? { filter: "blur(0px)", opacity: 1, y: 0 } : undefined
            }
            viewport={playOnView ? { once: true, amount: 0.2 } : undefined}
            exit={{ filter: "blur(10px)", opacity: 0, y: -12 }}
            transition={{
              duration,
              delay: index * delayStep,
            }}
            className={cn("inline-block", innerClassName)}
          >
            {item}
          </motion.span>
        )
      )}
    </AnimatePresence>
  );
  
}
